import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import api, { formatError } from "../lib/api";
import { Coins, Clock, Users, AlertCircle, ArrowLeft, Plus, Tag, X } from "lucide-react";

const CATEGORIES = [
  { value: "customer_growth", label: "Customer Growth" },
  { value: "hiring", label: "Hiring" },
  { value: "automation", label: "Automation" },
  { value: "local_business", label: "Local Business" },
  { value: "web_digital", label: "Web & Digital" },
  { value: "operations", label: "Operations" },
  { value: "finance", label: "Finance" },
  { value: "branding", label: "Branding" },
  { value: "personal", label: "Personal" },
];

export default function PostTaskPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [skillInput, setSkillInput] = useState("");
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "customer_growth",
    skills_required: [],
    credit_reward: 10,
    deadline_hours: 72,
    max_claims: 1,
  });

  useEffect(() => {
    if (user === null) navigate("/login");
    if (user) fetchWallet();
  }, [user]);

  const fetchWallet = async () => {
    try {
      const res = await api.get("/api/wallet/balance");
      setWallet(res.data);
    } catch (_) {}
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setNum = (key) => (e) => setForm((f) => ({ ...f, [key]: parseInt(e.target.value) || 0 }));

  const addSkill = () => {
    const s = skillInput.trim();
    if (s && !form.skills_required.includes(s)) {
      setForm((f) => ({ ...f, skills_required: [...f.skills_required, s] }));
    }
    setSkillInput("");
  };

  const removeSkill = (skill) => {
    setForm((f) => ({ ...f, skills_required: f.skills_required.filter((s) => s !== skill) }));
  };

  const totalCost = form.credit_reward * form.max_claims;
  const hasEnough = wallet && wallet.balance >= totalCost;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.title.trim() || !form.description.trim()) {
      setError("Title and description are required");
      return;
    }
    if (form.credit_reward <= 0) {
      setError("Credit reward must be positive");
      return;
    }
    if (!hasEnough) {
      setError("Insufficient credits. Top up your wallet first.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/api/orders/tasks", form);
      navigate("/my-work");
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  if (user === undefined) return null;

  return (
    <div style={{ paddingTop: 80, minHeight: "100vh" }}>
      <div style={{ maxWidth: 680, margin: "0 auto", padding: "0 24px 80px" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 32 }}>
          <button data-testid="post-task-back" onClick={() => navigate(-1)} style={{
            width: 38, height: 38, borderRadius: "50%", border: "1px solid var(--bg-neutral)",
            background: "rgba(255,255,255,0.6)", backdropFilter: "blur(10px)", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <ArrowLeft size={16} color="var(--text-muted)" />
          </button>
          <div>
            <h1 data-testid="post-task-title" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "1.5rem", letterSpacing: "-0.03em", color: "var(--text-main)" }}>
              Post a task
            </h1>
            <p style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.82rem", color: "var(--text-muted)" }}>
              Describe what you need done. Others will claim and execute it.
            </p>
          </div>
        </div>

        {/* Wallet indicator */}
        {wallet && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="glass-card-accent" style={{ padding: "1rem 1.4rem", marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Coins size={16} color="var(--accent)" strokeWidth={1.5} />
              <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                Available: <strong style={{ color: "var(--text-main)" }}>{wallet.balance}</strong> credits
              </span>
            </div>
            {totalCost > 0 && (
              <span data-testid="post-task-cost" style={{
                fontFamily: "'Manrope', sans-serif", fontSize: "0.82rem", fontWeight: 600,
                color: hasEnough ? "var(--success)" : "#B55A5A",
              }}>
                Cost: {totalCost} credits
              </span>
            )}
          </motion.div>
        )}

        {error && (
          <div data-testid="post-task-error" style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "rgba(181,90,90,0.06)", border: "1px solid rgba(181,90,90,0.15)", borderRadius: 10, padding: "0.65rem 0.9rem", marginBottom: 20 }}>
            <AlertCircle size={14} color="#B55A5A" strokeWidth={1.5} style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: "0.8rem", color: "#B55A5A", fontFamily: "'Manrope', sans-serif" }}>{error}</span>
          </div>
        )}

        {/* Form */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <form onSubmit={handleSubmit} className="glass-card" style={{ padding: "2rem" }}>
            {/* Title */}
            <div style={{ marginBottom: "1.2rem" }}>
              <label className="form-label">Task title *</label>
              <input data-testid="post-task-title-input" className="form-input" value={form.title} onChange={set("title")}
                placeholder="e.g. Build a landing page for my SaaS" required />
            </div>

            {/* Description */}
            <div style={{ marginBottom: "1.2rem" }}>
              <label className="form-label">Description *</label>
              <textarea data-testid="post-task-desc-input" className="form-input" value={form.description} onChange={set("description")}
                placeholder="Describe the task in detail. What needs to be done? What does success look like?"
                required rows={4} style={{ resize: "vertical" }} />
            </div>

            {/* Category + Deadline row */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: "1.2rem" }}>
              <div>
                <label className="form-label">Category</label>
                <select data-testid="post-task-category" className="form-input" value={form.category} onChange={set("category")}>
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Deadline (hours)</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input data-testid="post-task-deadline" type="number" className="form-input" value={form.deadline_hours} onChange={setNum("deadline_hours")} min={1} style={{ flex: 1 }} />
                  <Clock size={14} color="var(--text-muted)" />
                </div>
              </div>
            </div>

            {/* Credit reward + Max claims row */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: "1.2rem" }}>
              <div>
                <label className="form-label">Credit reward per claim *</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input data-testid="post-task-reward" type="number" className="form-input" value={form.credit_reward} onChange={setNum("credit_reward")} min={1} style={{ flex: 1 }} />
                  <Coins size={14} color="var(--accent)" />
                </div>
              </div>
              <div>
                <label className="form-label">Max claims</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input data-testid="post-task-max-claims" type="number" className="form-input" value={form.max_claims} onChange={setNum("max_claims")} min={1} max={10} style={{ flex: 1 }} />
                  <Users size={14} color="var(--text-muted)" />
                </div>
              </div>
            </div>

            {/* Skills */}
            <div style={{ marginBottom: "1.5rem" }}>
              <label className="form-label">Skills required (optional)</label>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <input data-testid="post-task-skill-input" className="form-input" value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }}
                  placeholder="Type a skill and press Enter" style={{ flex: 1 }} />
                <button type="button" data-testid="post-task-add-skill" onClick={addSkill} className="btn-secondary"
                  style={{ display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                  <Plus size={13} /> Add
                </button>
              </div>
              {form.skills_required.length > 0 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {form.skills_required.map((skill) => (
                    <span key={skill} data-testid={`skill-tag-${skill}`} style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      padding: "3px 10px", borderRadius: 100, background: "var(--accent-subtle)",
                      border: "1px solid rgba(59,130,246,0.12)", fontFamily: "'Manrope', sans-serif",
                      fontSize: "0.75rem", fontWeight: 500, color: "var(--accent-text)",
                    }}>
                      <Tag size={10} /> {skill}
                      <button type="button" onClick={() => removeSkill(skill)} style={{
                        background: "none", border: "none", cursor: "pointer", padding: 0,
                        display: "flex", alignItems: "center", color: "var(--text-muted)",
                      }}><X size={11} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Submit */}
            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" className="btn-secondary" onClick={() => navigate(-1)} style={{ flex: 1 }}>Cancel</button>
              <button type="submit" data-testid="post-task-submit" className="btn-primary" disabled={loading || !hasEnough}
                style={{ flex: 2, padding: "0.7rem", borderRadius: 12, opacity: loading || !hasEnough ? 0.5 : 1 }}>
                {loading ? "Posting..." : `Post task (${totalCost} credits)`}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
